'use client';

import { useCallback, useState, useRef, useEffect } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { useRouter, useSearchParams } from 'next/navigation';

import Button from '@sympla/sympla-components/Button';
import { IconClose } from '@sympla/sympla-components';
import IconSearch from 'assets/icons/ic-search.svg';

import { createLinkToRedirect } from 'utils/createLinkToRedirect';
import useDebounce from 'hooks/useDebounce';
import { ALGOLIA_NUMBER_CARACTER } from 'constants/algolia';
import SearchSuggestions from 'components/SearchSuggestions/SearchSuggestions';
import { isMobile as getIsMobile } from 'utils/misc';
import * as styles from './AlgoliaSearch.css';
import { ANYWHERE_LOCATION_JSON } from 'constants/index';
import classNames from 'classnames';

const DEBOUNCE_TIME = 300;
const SEARCH_HASH = '#pesquisar';

function AlgoliaSearch({
  isNavbarCollapsed = false,
  isDark = false,
  hasMossColor = false
}: {
  isNavbarCollapsed?: boolean;
  isDark?: boolean;
  hasMossColor?: boolean;
}) {
  const { query, refine } = useSearchBox();
  const [inputValue, setInputValue] = useState('');
  const debouncedSearchTerm = useDebounce(inputValue, DEBOUNCE_TIME);

  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchParams = useSearchParams();
  const currentSearchParams = searchParams || new URLSearchParams();

  const searchText = currentSearchParams.get('s');
  const searchTab = currentSearchParams.get('tab');

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const checkHash = () => {
      if (typeof window !== 'undefined') {
        setIsDropdownOpen(window.location.hash === SEARCH_HASH);
      }
    };

    checkHash();

    window.addEventListener('hashchange', checkHash);

    return () => {
      window.removeEventListener('hashchange', checkHash);
    };
  }, []);

  useEffect(() => {
    if (isDropdownOpen && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDropdownOpen]);

  const buttonClass = classNames(styles.searchButton, {
    [styles.searchButtonDark]: isDark,
    [styles.colorMoss]: hasMossColor
  });

  const iconClass = classNames(styles.cleanInput, {
    [styles.iconMoss]: hasMossColor
  });

  const searchFormClass = classNames(styles.searchForm, {
    [styles.collapsedSearchForm]: isNavbarCollapsed,
    [styles.searchFormDark]: isDark,
    [styles.searchFormMoss]: hasMossColor
  });

  const openDropdown = useCallback(() => {
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
      if (typeof window !== 'undefined') {
        window.history.pushState(null, '', SEARCH_HASH);
      }
    }
  }, [isDropdownOpen]);

  const closeDropdown = useCallback(() => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
      if (typeof window !== 'undefined') {
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.pushState(null, '', cleanUrl);
      }
    }
  }, [isDropdownOpen]);

  const handleShowMore = useCallback(() => {
    setIsDropdownOpen(false);
    window.location.href = createLinkToRedirect({
      param: `?s=${inputValue}`,
      routeType: 'search-bar-default-filter',
      userLocation: ANYWHERE_LOCATION_JSON
    });
  }, [inputValue]);

  function handleSearch(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    window.Intercom('trackEvent', 'Fez uma busca');
    handleShowMore();
  }

  function handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' && inputValue.trim().length > 0) {
      window.Intercom('trackEvent', 'Fez uma busca');
      handleShowMore();
    } else if (event.key && !isDropdownOpen) {
      openDropdown();
    }
  }

  useEffect(() => {
    if (debouncedSearchTerm.length > ALGOLIA_NUMBER_CARACTER) {
      refine(debouncedSearchTerm);
      return;
    }

    if (debouncedSearchTerm.length === 0 && query !== '') {
      refine('');
    }
  }, [debouncedSearchTerm, refine, query]);

  useEffect(() => {
    setInputValue(searchText || '');
  }, []);

  useEffect(() => {
    if (searchTab) closeDropdown();
  }, [searchTab, closeDropdown]);

  const handleClickInside = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleClickOutside = () => {
    closeDropdown();
  };

  const handleCloseDropdown = useCallback(() => {
    closeDropdown();
  }, [closeDropdown]);

  const handleFocus = () => {
    if (!isDropdownOpen) {
      openDropdown();
    }
  };

  return (
    <div
      className={`${styles.searchWrapper} ${
        isNavbarCollapsed && styles.collapsedSearchWrapper
      } `}
      data-testid="search-algolia-wrapper"
    >
      <form
        ref={formRef}
        className={searchFormClass}
        onSubmit={handleSearch}
        role="presentation"
        data-testid="search-algolia-form"
      >
        <IconSearch
          width={24}
          height={24}
          className={`${styles.searchIcon} ${
            isDark ? styles.searchIconDark : ''
          }`}
        />
        <input
          ref={inputRef}
          className={`${styles.searchInput} ${
            isDark ? styles.searchInputDark : ''
          }`}
          placeholder="Buscar experiências"
          type="text"
          onChange={handleTextChange}
          value={inputValue}
          onKeyDown={handleKeyDown}
          data-testid="search-algolia-input"
          onFocus={handleFocus}
        />
        {inputValue && (
          <div className={styles.searchButtons}>
            <Button
              variant="text"
              type="submit"
              className={buttonClass}
              data-testid="search-algolia-submit-button"
            >
              Buscar
            </Button>
            <IconClose
              width={20}
              height={20}
              className={iconClass}
              onClick={() => {
                setInputValue('');
                refine('');
                inputRef.current?.focus();
              }}
            />
          </div>
        )}
      </form>
      {isDropdownOpen && (
        <SearchSuggestions
          onClickInside={handleClickInside}
          setInputValue={setInputValue}
          handleTextChange={handleTextChange}
          inputValue={inputValue}
          inputRef={inputRef}
          onClose={handleCloseDropdown}
          formRef={formRef}
          hasMossColor={hasMossColor}
          handleSearch={handleSearch}
          isNavbarCollapsed={isNavbarCollapsed}
          handleClickOutside={handleClickOutside}
        />
      )}
    </div>
  );
}

export default AlgoliaSearch;
